import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Phone, Mail, Clock, Zap, Home, Building2 } from "lucide-react";
import BrandHeader from "@/components/BrandHeader";
import ProgressBar from "@/components/ProgressBar";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Link } from "react-router-dom";

const TOTAL_STEPS = 5;

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
  transition: { duration: 0.35, ease: "easeOut" },
};

export default function Quiz() {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState({
    owner_type: "",
    bill_over_100: null,
    zip_code: "",
    address: "",
    name: "",
    phone: "",
    email: "",
  });

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const goNext = () => setStep((s) => Math.min(s + 1, 6));
  const goBack = () => setStep((s) => Math.max(s - 1, 1));

  const submit = async () => {
    setError("");
    if (!data.name.trim() || !data.phone.trim()) {
      setError("Name and phone are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/leads", {
        owner_type: data.owner_type,
        bill_over_100: !!data.bill_over_100,
        zip_code: data.zip_code,
        address: data.address,
        name: data.name.trim(),
        phone: data.phone.trim(),
        email: data.email.trim() || null,
      });
      setStep(6);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#F8FAFC] flex flex-col"
      style={{ fontFamily: "Montserrat, sans-serif" }}
      data-testid="quiz-root"
    >
      <BrandHeader />

      <main className="flex-1 flex items-start justify-center py-8 sm:py-12 px-4">
        <div className="w-full max-w-2xl">
          {step <= TOTAL_STEPS && (
            <div className="mb-6">
              <ProgressBar current={step} total={TOTAL_STEPS} />
            </div>
          )}

          <div
            data-testid="quiz-card"
            className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] p-6 sm:p-10 md:p-12 border border-slate-100"
          >
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div key="s1" {...fadeUp} data-testid="quiz-step-1">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#FF8800]">
                    Let&apos;s get started
                  </span>
                  <h1 className="mt-2 text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#001B33] leading-tight">
                    Are you a homeowner or business owner?
                  </h1>
                  <div className="mt-2 h-1 w-16 bg-[#FF8800] rounded-full" />
                  <p className="mt-6 text-base md:text-lg text-slate-600 font-medium">
                    We&apos;ll tailor your solar estimate to fit your property.
                  </p>
                  <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                      data-testid="quiz-step1-homeowner-btn"
                      onClick={() => {
                        update({ owner_type: "homeowner" });
                        goNext();
                      }}
                      className="group border-2 border-slate-200 hover:border-[#FF8800] bg-white hover:bg-orange-50 text-[#001B33] font-semibold rounded-xl py-8 px-6 transition-all active:scale-[0.98] flex flex-col items-center justify-center gap-3"
                    >
                      <span className="h-14 w-14 rounded-full bg-orange-100 group-hover:bg-[#FF8800] flex items-center justify-center transition-colors">
                        <Home className="h-7 w-7 text-[#FF8800] group-hover:text-white transition-colors" />
                      </span>
                      <span className="text-xl">Homeowner</span>
                    </button>
                    <button
                      data-testid="quiz-step1-business-btn"
                      onClick={() => {
                        update({ owner_type: "business" });
                        goNext();
                      }}
                      className="group border-2 border-slate-200 hover:border-[#FF8800] bg-white hover:bg-orange-50 text-[#001B33] font-semibold rounded-xl py-8 px-6 transition-all active:scale-[0.98] flex flex-col items-center justify-center gap-3"
                    >
                      <span className="h-14 w-14 rounded-full bg-orange-100 group-hover:bg-[#FF8800] flex items-center justify-center transition-colors">
                        <Building2 className="h-7 w-7 text-[#FF8800] group-hover:text-white transition-colors" />
                      </span>
                      <span className="text-xl">Business Owner</span>
                    </button>
                  </div>
                </motion.div>
              )}


              {step === 2 && (
                <motion.div key="s2" {...fadeUp} data-testid="quiz-step-2">
                  <div className="flex items-center gap-2 mb-3">
                    <Zap className="h-5 w-5 text-[#FF8800]" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-[#FF8800]">
                      Quick savings check
                    </span>
                  </div>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-[#001B33] leading-tight">
                    Is your monthly electric bill over $100?
                  </h1>
                  <div className="mt-2 h-1 w-16 bg-[#FF8800] rounded-full" />
                  <p className="mt-6 text-base md:text-lg text-slate-600 font-medium">
                    Most homes with bills over $100/mo qualify for significant solar savings.
                  </p>
                  <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                      data-testid="quiz-step1-yes-btn"
                      onClick={() => {
                        update({ bill_over_100: true });
                        goNext();
                      }}
                      className="group border-2 border-slate-200 hover:border-[#FF8800] bg-white hover:bg-orange-50 text-[#001B33] font-semibold rounded-xl py-8 px-6 transition-all active:scale-[0.98] flex flex-col items-center justify-center gap-3"
                    >
                      <span className="h-12 w-12 rounded-full bg-orange-100 group-hover:bg-[#FF8800] text-[#FF8800] group-hover:text-white flex items-center justify-center text-2xl font-semibold transition-colors">
                        ✓
                      </span>
                      <span className="text-xl">Yes</span>
                    </button>
                    <button
                      data-testid="quiz-step1-no-btn"
                      onClick={() => {
                        update({ bill_over_100: false });
                        goNext();
                      }}
                      className="group border-2 border-slate-200 hover:border-[#FF8800] bg-white hover:bg-orange-50 text-[#001B33] font-semibold rounded-xl py-8 px-6 transition-all active:scale-[0.98] flex flex-col items-center justify-center gap-3"
                    >
                      <span className="h-12 w-12 rounded-full bg-slate-100 group-hover:bg-[#FF8800] text-[#001B33] group-hover:text-white flex items-center justify-center text-2xl font-semibold transition-colors">
                        ✕
                      </span>
                      <span className="text-xl">No</span>
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div key="s3" {...fadeUp} data-testid="quiz-step-3">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#FF8800]">
                    Local incentives
                  </span>
                  <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-[#001B33]">
                    What&apos;s your zip code?
                  </h2>
                  <div className="mt-2 h-1 w-16 bg-[#FF8800] rounded-full" />
                  <p className="mt-6 text-base md:text-lg text-slate-600 font-medium">
                    We use this to check rebates and incentives available in your area.
                  </p>
                  <input
                    data-testid="quiz-step2-zip-input"
                    type="text"
                    inputMode="numeric"
                    maxLength={5}
                    value={data.zip_code}
                    onChange={(e) =>
                      update({ zip_code: e.target.value.replace(/\D/g, "") })
                    }
                    placeholder="e.g. 19103"
                    className="mt-8 w-full border-2 border-slate-200 rounded-lg p-4 text-2xl font-semibold tracking-widest text-center focus:border-[#FF8800] focus:ring-1 focus:ring-[#FF8800] transition-colors outline-none"
                  />
                  <NavButtons
                    onBack={goBack}
                    onNext={goNext}
                    disabled={data.zip_code.length < 5}
                    nextLabel="Next"
                  />
                </motion.div>
              )}

              {step === 4 && (
                <motion.div key="s4" {...fadeUp} data-testid="quiz-step-4">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#FF8800]">
                    Your home
                  </span>
                  <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-[#001B33]">
                    Where should we check?
                  </h2>
                  <div className="mt-2 h-1 w-16 bg-[#FF8800] rounded-full" />
                  <p className="mt-6 text-base md:text-lg text-slate-600 font-medium">
                    Start typing your address — we&apos;ll auto-complete it.
                  </p>
                  <div className="mt-8">
                    <AddressAutocomplete
                      value={data.address}
                      onChange={(v) => update({ address: v })}
                    />
                  </div>
                  <NavButtons
                    onBack={goBack}
                    onNext={goNext}
                    disabled={data.address.trim().length < 5}
                    nextLabel="Next"
                  />
                </motion.div>
              )}

              {step === 5 && (
                <motion.div key="s5" {...fadeUp} data-testid="quiz-step-5">
                  <span className="text-xs font-semibold uppercase tracking-wider text-[#FF8800]">
                    Almost done
                  </span>
                  <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-[#001B33]">
                    See your custom estimate
                  </h2>
                  <div className="mt-2 h-1 w-16 bg-[#FF8800] rounded-full" />
                  <p className="mt-6 text-base md:text-lg text-slate-600 font-medium">
                    Where should we send your free estimate?
                  </p>

                  <div className="mt-8 space-y-5">
                    <Field label="Full Name *">
                      <input
                        data-testid="quiz-step4-name-input"
                        type="text"
                        value={data.name}
                        onChange={(e) => update({ name: e.target.value })}
                        placeholder="Jane Doe"
                        className="w-full border-2 border-slate-200 rounded-lg p-4 text-lg font-medium focus:border-[#FF8800] focus:ring-1 focus:ring-[#FF8800] transition-colors outline-none"
                      />
                    </Field>
                    <Field label="Phone Number *">
                      <input
                        data-testid="quiz-step4-phone-input"
                        type="tel"
                        value={data.phone}
                        onChange={(e) => update({ phone: e.target.value })}
                        placeholder="(555) 123-4567"
                        className="w-full border-2 border-slate-200 rounded-lg p-4 text-lg font-medium focus:border-[#FF8800] focus:ring-1 focus:ring-[#FF8800] transition-colors outline-none"
                      />
                    </Field>
                    <Field label="Email (optional)">
                      <input
                        data-testid="quiz-step4-email-input"
                        type="email"
                        value={data.email}
                        onChange={(e) => update({ email: e.target.value })}
                        placeholder="you@example.com"
                        className="w-full border-2 border-slate-200 rounded-lg p-4 text-lg font-medium focus:border-[#FF8800] focus:ring-1 focus:ring-[#FF8800] transition-colors outline-none"
                      />
                    </Field>
                  </div>

                  {error && (
                    <p
                      data-testid="quiz-error"
                      className="mt-4 text-sm font-semibold text-red-600"
                    >
                      {error}
                    </p>
                  )}

                  <div className="mt-8 flex flex-col sm:flex-row gap-3">
                    <button
                      data-testid="quiz-back-btn"
                      onClick={goBack}
                      className="sm:w-32 border-2 border-slate-200 hover:border-[#001B33] text-[#001B33] font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <ArrowLeft className="h-4 w-4" /> Back
                    </button>
                    <button
                      data-testid="quiz-submit-btn"
                      onClick={submit}
                      disabled={submitting || !data.name.trim() || !data.phone.trim()}
                      className="flex-1 bg-[#FF8800] hover:bg-[#E67A00] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-4 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all active:scale-[0.99] text-lg flex justify-center items-center gap-2"
                    >
                      {submitting ? "Submitting…" : "See Your Custom Estimate"}
                      {!submitting && <ArrowRight className="h-5 w-5" />}
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 6 && (
                <motion.div
                  key="s6"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                  data-testid="quiz-success"
                  className="text-center"
                >
                  <div className="mx-auto h-20 w-20 rounded-full bg-orange-100 flex items-center justify-center">
                    <CheckCircle2 className="h-12 w-12 text-[#FF8800]" />
                  </div>
                  <h2 className="mt-6 text-3xl sm:text-4xl font-semibold tracking-tight text-[#001B33]">
                    You&apos;re all set!
                  </h2>
                  <div className="mx-auto mt-3 h-1 w-16 bg-[#FF8800] rounded-full" />
                  <p className="mt-6 text-base md:text-lg text-slate-600 font-medium">
                    Thanks, <span className="text-[#001B33] font-semibold">{data.name.split(" ")[0]}</span>!
                    We&apos;ll get back to you within 24 hours with your custom solar estimate.
                  </p>

                  <div className="mt-8 bg-[#001B33] rounded-xl p-6 text-left">
                    <div className="flex items-center gap-2 text-[#FF8800] text-xs font-semibold uppercase tracking-wider">
                      <Clock className="h-4 w-4" /> In the meantime, reach us at:
                    </div>
                    <div className="mt-4 space-y-3">
                      <a
                        data-testid="success-phone-link"
                        href="tel:+16105055200"
                        className="flex items-center gap-3 text-white hover:text-[#FF8800] transition-colors font-semibold"
                      >
                        <span className="h-9 w-9 rounded-full bg-[#FF8800]/20 flex items-center justify-center">
                          <Phone className="h-4 w-4 text-[#FF8800]" />
                        </span>
                        (610) 505-5200
                      </a>
                      <a
                        data-testid="success-email-link"
                        href="mailto:solfish1125@gmail.com"
                        className="flex items-center gap-3 text-white hover:text-[#FF8800] transition-colors font-semibold break-all"
                      >
                        <span className="h-9 w-9 rounded-full bg-[#FF8800]/20 flex items-center justify-center flex-shrink-0">
                          <Mail className="h-4 w-4 text-[#FF8800]" />
                        </span>
                        solfish1125@gmail.com
                      </a>
                    </div>
                  </div>

                  <button
                    data-testid="quiz-restart-btn"
                    onClick={() => {
                      setData({
                        owner_type: "",
                        bill_over_100: null,
                        zip_code: "",
                        address: "",
                        name: "",
                        phone: "",
                        email: "",
                      });
                      setStep(1);
                    }}
                    className="mt-6 text-sm font-semibold text-slate-500 hover:text-[#FF8800] transition-colors"
                  >
                    Start a new estimate
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <p className="mt-6 text-center text-xs text-slate-500 font-medium">
            © {new Date().getFullYear()} Solar Installation Specialists
          </p>
        </div>
      </main>

      <Link
        to="/admin/login"
        data-testid="footer-login-link"
        className="fixed bottom-3 left-3 text-[10px] text-slate-400 hover:text-[#FF8800] font-medium tracking-wider uppercase transition-colors opacity-60 hover:opacity-100"
      >
        login
      </Link>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-[#001B33] mb-2 uppercase tracking-wider">
        {label}
      </span>
      {children}
    </label>
  );
}

function NavButtons({ onBack, onNext, disabled, nextLabel }) {
  return (
    <div className="mt-8 flex flex-col sm:flex-row gap-3">
      <button
        data-testid="quiz-back-btn"
        onClick={onBack}
        className="sm:w-32 border-2 border-slate-200 hover:border-[#001B33] text-[#001B33] font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <button
        data-testid="quiz-next-btn"
        onClick={onNext}
        disabled={disabled}
        className="flex-1 bg-[#FF8800] hover:bg-[#E67A00] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-4 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all active:scale-[0.99] text-lg flex justify-center items-center gap-2"
      >
        {nextLabel} <ArrowRight className="h-5 w-5" />
      </button>
    </div>
  );
}
